import React, { useEffect, useMemo, useState } from "react";
import { Col, Row } from "reactstrap";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogContentText,
  IconButton,
  Stack,
  Collapse,
  Card,
  CardContent,
  Typography,
  Grid,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";

import {
  utilityTunnelAdminRoles,
  utilityTunnelConsumerRoles,
} from "common/constant";
import Backdrop from "Components/Common/Backdrop";
import CommentInput from "Components/Common/CommentInput";
import CommentBox from "Components/Common/CommentBox";
import { ApplicationApprovalTimelineChart } from "common/ApplicationApprovalTimelineChart";

import {
  useLazyGetApplicationCommentQuery,
  usePostApplicationCommentMutation,
} from "apis/ApplicationRequestComment";

import { getImageUrl, isAuthorizedUser, isCommentInputVisible } from "utils";

import { DialogHeader } from "./style";
import { useGetAccessCardApplicationDetailQuery } from "apis/UtilityTunnel/ApplicationRequest";
import { getOptionLabel, getOptionValue } from "common/enums";
import AccessHolderView from "./AccessHolderView";
import AccessCardDetailView from "./AccessCardDetailView";
import UtilityTunnelApprovalActionModal from "./UtilityTunnelApprovalActionModal";
import { ProfileImage } from "Components/ApplicationViewModal/style";
import { DigitalSignatureContainer } from "pages/Profile/style";
import AdminDiscussion from "common/AdminDiscussion";

const UtilityTunnelApplicationViewModal = ({
  handleClose,
  applicationId,
  t,
}) => {
  const {
    data: { data: applicationData = {} } = {},
    isLoading: isConsumerDataLoading,
    isFetching,
    isError,
  } = useGetAccessCardApplicationDetailQuery(applicationId);
  const { employeeContractorDetail } = applicationData || {};

  const [getAdminComments, { data: { data: commentData } = {} }] =
    useLazyGetApplicationCommentQuery();

  const profileURL = useMemo(
    () =>
      employeeContractorDetail?.profileImageUrl
        ? getImageUrl(employeeContractorDetail?.profileImageUrl)
        : "/broken-image.jpg",
    [employeeContractorDetail?.profileImageUrl]
  );

  const signatureUrl = useMemo(
    () =>
      employeeContractorDetail?.signatureImageUrl
        ? getImageUrl(employeeContractorDetail?.signatureImageUrl)
        : "/broken-image.jpg",
    [employeeContractorDetail?.signatureImageUrl]
  );

  const [addComment] = usePostApplicationCommentMutation();

  const { roles } = useSelector((state) => state.Login);

  const [openDiscussion, setOpenDiscussion] = useState(true);

  const isAdmin = useMemo(
    () => !isAuthorizedUser(roles, utilityTunnelConsumerRoles),
    [roles]
  );

  const handleDiscussionCollapse = () => {
    setOpenDiscussion(!openDiscussion);
  };

  const addNewComment = async (comment) => {
    await addComment(
      {
        accessDetailId: applicationData?.id,
        comment,
      },
      {},
      false,
      true
    );
  };

  useEffect(() => {
    if (commentData?.length >= 5) setOpenDiscussion((prev) => !prev);
  }, [commentData]);

  useEffect(() => {
    if (
      applicationId &&
      isAuthorizedUser(roles, [
        ...utilityTunnelAdminRoles,
        ...utilityTunnelConsumerRoles,
      ])
    ) {
      (async () => {
        try {
          await getAdminComments(applicationId, {}, false, true);
        } catch (error) {
          console.error(error);
        }
      })();
    }
  }, [applicationId, roles]);

  useEffect(() => {
    if (isError) {
      handleClose();
    }
  }, [isError]);

  if (isConsumerDataLoading || isFetching) {
    return <Backdrop open />;
  }

  return (
    <Dialog
      open={!!applicationId}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      scroll="paper"
    >
      <DialogHeader>
        {t("Access Card Details")}
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogHeader>
      <DialogContent>
        <DialogContentText variant="subtitle2" component="div">
          <div className="mt-2 ">
            <>
              <div className="mb-3">
                <Grid
                  container
                  columnSpacing={2}
                  alignItems="center"
                  justifyContent="center"
                >
                  <Grid item xs={12} md={6}>
                    <DigitalSignatureContainer>
                      <ProfileImage src={profileURL} />
                    </DigitalSignatureContainer>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <img
                      src={signatureUrl}
                      style={{ width: 276, height: 119, objectFit: "contain" }}
                    />
                  </Grid>
                </Grid>
              </div>
            </>
            <Row className="d-flex  mb-2">
              <Col lg={6} className="mb-1">
                <Row>
                  <Col md={5}>{t("Access Card Holder Type")}:</Col>
                  <Col
                    md={7}
                    className="fw-semibold"
                    style={{ wordBreak: "break-word" }}
                  >
                    {getOptionLabel(
                      "utilityTunnelConsumerType",
                      applicationData?.accessCardHolderType
                    ) ?? "--"}
                  </Col>
                </Row>
              </Col>
            </Row>

            {!!applicationData && (
              <AccessHolderView
                accessHolderData={
                  applicationData?.employeeContractorDetail ?? {}
                }
                accessCardHolderType={applicationData?.accessCardHolderType}
              />
            )}

            {!!applicationData && (
              <AccessCardDetailView
                accessHolderData={applicationData ?? {}}
                accessCardHolderType={applicationData?.accessCardHolderType}
              />
            )}
          </div>

          <div
            className="fs-4 fw-semibold mb-3 border-bottom"
            onClick={handleDiscussionCollapse}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <div>{t("Discussion")}</div>{" "}
              <p>{openDiscussion ? <ExpandLess /> : <ExpandMore />}</p>
            </Stack>
          </div>

          <Collapse in={openDiscussion} timeout="auto" unmountOnExit>
            {/* {!!applicationData?.utaCardIssuanceStatus?.name &&
              isCommentInputVisible(
                applicationData?.utaCardIssuanceStatus?.name,
                roles,
                !!commentData?.length,
                false,
                true
              ) && (
                <CommentInput
                  handleSubmit={addNewComment}
                  applicationId={applicationData?.id ?? ""}
                />
              )} */}
            {commentData?.length ? (
              commentData.map(({ comment, createdAt, name }, index) => (
                <CommentBox
                  comment={comment}
                  name={name}
                  createdAt={createdAt}
                  key={index}
                />
              ))
            ) : (
              <Card variant="outlined" className="mt-2 p-0">
                <CardContent className="pt-1">
                  <Typography color="GrayText" gutterBottom>
                    {t("No comment")}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Collapse>

          {isAdmin && (
            <AdminDiscussion
              isUtilityTunnel
              buildingInformationId={applicationData.id}
            />
          )}

          {isAdmin && !!applicationData?.id && (
            <ApplicationApprovalTimelineChart
              applicationId={applicationData.id}
              applicationStatus={applicationData.applicationStatus?.name}
              isUtilityTunnel
            />
          )}
        </DialogContentText>
      </DialogContent>

      {applicationData && (
        <UtilityTunnelApprovalActionModal
          applicationData={applicationData}
          handleClose={handleClose}
        />
      )}
    </Dialog>
  );
};

UtilityTunnelApplicationViewModal.propTypes = {
  handleClose: PropTypes.func,
  applicationId: PropTypes.string,
  t: PropTypes.func,
};

export default withTranslation()(UtilityTunnelApplicationViewModal);
